import type { MediaDerivativeKind, MediaKind } from './media-asset';

export const MEDIA_TASK_VERSION = 1 as const;
export const MEDIA_TASK_PROCESSING_STATUS = 'processing' as const;

export type MediaTaskKind =
  | 'image-derivative'
  | 'video-poster'
  | 'batch-export';

export type DerivativeMediaTaskKind = Exclude<MediaTaskKind, 'batch-export'>;

export const MEDIA_DERIVATIVE_POLICY_VERSION = 1 as const;

export function buildMediaTaskId(params: {
  kind: DerivativeMediaTaskKind;
  jobId: string;
  assetId: string;
}) {
  return `${params.kind}:${params.jobId}:${params.assetId}`;
}

export type MediaTaskSource = {
  assetId: string;
  kind: MediaKind;
  key: string;
};

type MediaTaskBase<TKind extends MediaTaskKind> = {
  v: typeof MEDIA_TASK_VERSION;
  taskId: string;
  kind: TKind;
  requestedAt: string;
};

export type ImageDerivativeTask = MediaTaskBase<'image-derivative'> & {
  source: MediaTaskSource & { kind: 'image' };
  derivatives: Array<{
    kind: Extract<
      MediaDerivativeKind,
      'thumbnail' | 'smallPreview' | 'preview' | 'largePreview'
    >;
    maxWidth: number;
    outputKey: string;
    contentType: 'image/webp' | 'image/avif';
    quality: number;
  }>;
};

export type VideoPosterTask = MediaTaskBase<'video-poster'> & {
  source: MediaTaskSource & { kind: 'video' };
  poster: {
    kind: 'poster';
    maxWidth: number;
    seekPercent: number;
    outputKey: string;
    contentType: 'image/webp';
    quality: number;
  };
  thumbnail: {
    kind: 'thumbnail';
    maxWidth: number;
    outputKey: string;
    contentType: 'image/webp';
    quality: number;
  };
};

export type BatchExportTask = MediaTaskBase<'batch-export'> & {
  exportId: string;
  fingerprint: string;
  sources: Array<
    MediaTaskSource & {
      name: string;
      size: number;
      etag: string;
      expiresAt: string;
    }
  >;
  outputKey: string;
};

export type MediaTask = ImageDerivativeTask | VideoPosterTask | BatchExportTask;

export type MediaTaskOutput = {
  key: string;
  mimeType: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  derivativeKind?: MediaDerivativeKind;
};

export type MediaTaskResult = {
  v: typeof MEDIA_TASK_VERSION;
  taskId: string;
  taskKind: MediaTaskKind;
  outputs: MediaTaskOutput[];
};
